import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Activity tracking for session timeout
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [activityTimer, setActivityTimer] = useState(null);
  const [sessionTimeoutWarning, setSessionTimeoutWarning] = useState(false);

  // Session timeout constants
  const SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
  const WARNING_TIME = 2 * 60 * 1000; // Show warning 2 minutes before timeout
  const ACTIVITY_UPDATE_INTERVAL = 30 * 1000; // Update activity every 30 seconds

  // Initialize authentication state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('authUser');
        const storedLastActivity = localStorage.getItem('lastActivity');

        if (storedToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          const lastActivityTime = storedLastActivity ? parseInt(storedLastActivity) : Date.now();
          
          // Check if session has expired
          const timeSinceActivity = Date.now() - lastActivityTime;
          
          if (timeSinceActivity < SESSION_TIMEOUT) {
            setToken(storedToken);
            setUser(parsedUser);
            setIsAuthenticated(true);
            setLastActivity(Date.now());
            
            // Set up axios default authorization header
            axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            
            // Start activity monitoring
            startActivityMonitoring();
          } else {
            // Session expired, clear stored data
            clearAuthData();
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Clear authentication data
  const clearAuthData = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setSessionTimeoutWarning(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('lastActivity');
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear activity timer
    if (activityTimer) {
      clearInterval(activityTimer);
      setActivityTimer(null);
    }
  }, [activityTimer]);

  // Update user activity
  const updateActivity = useCallback(async () => {
    const now = Date.now();
    setLastActivity(now);
    localStorage.setItem('lastActivity', now.toString());
    
    // Update activity on server if authenticated
    if (token && isAuthenticated) {
      try {
        await axios.post(`${API}/api/auth/activity`, {
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Failed to update server activity:', error);
        // Intelligent 401 handling - verify token before logout
        if (error.response?.status === 401) {
          // Verify if token is actually invalid by checking with /api/auth/me
          try {
            await axios.get(`${API}/api/auth/me`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            // Token is still valid, 401 was due to other issue - don't logout
            console.log('Token still valid, activity update failed for other reason');
          } catch (verifyError) {
            // Token is truly invalid, proceed with logout
            if (verifyError.response?.status === 401) {
              console.log('Token verification failed, logging out user');
              logout();
            }
          }
        }
      }
    }
  }, [token, isAuthenticated]);

  // Start activity monitoring
  const startActivityMonitoring = useCallback(() => {
    // Clear existing timer
    if (activityTimer) {
      clearInterval(activityTimer);
    }

    // Set up new timer
    const timer = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivity;
      
      if (timeSinceActivity >= SESSION_TIMEOUT) {
        // Session expired
        logout();
      } else if (timeSinceActivity >= SESSION_TIMEOUT - WARNING_TIME) {
        // Show warning
        setSessionTimeoutWarning(true);
      }
    }, 1000); // Check every second

    setActivityTimer(timer);

    // Set up periodic activity updates to server
    const activityUpdateTimer = setInterval(() => {
      if (isAuthenticated) {
        updateActivity();
      }
    }, ACTIVITY_UPDATE_INTERVAL);

    return () => {
      clearInterval(timer);
      clearInterval(activityUpdateTimer);
    };
  }, [lastActivity, isAuthenticated, updateActivity]);

  // Activity event handlers
  const handleUserActivity = useCallback(() => {
    updateActivity();
    setSessionTimeoutWarning(false);
  }, [updateActivity]);

  // Set up activity listeners
  useEffect(() => {
    if (isAuthenticated) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      events.forEach(event => {
        document.addEventListener(event, handleUserActivity, true);
      });

      startActivityMonitoring();

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleUserActivity, true);
        });
      };
    }
  }, [isAuthenticated, handleUserActivity, startActivityMonitoring]);

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/auth/login`, {
        email,
        password
      });

      if (response.data.success) {
        if (response.data.requires2FA) {
          // Extract development code from message if present
          let developmentCode = null;
          const message = response.data.message || '';
          if (message.includes('DEVELOPMENT MODE') && message.includes('Your 2FA code is')) {
            // Extract code from message like "DEVELOPMENT MODE: Your 2FA code is 123456"
            const codeMatch = message.match(/Your 2FA code is (\d{6})/);
            if (codeMatch) {
              developmentCode = codeMatch[1];
            }
          }
          
          // Return 2FA requirement without setting authentication
          return {
            success: true,
            requires2FA: true,
            message: response.data.message,
            developmentCode: developmentCode
          };
        } else {
          // Set authentication data
          const { access_token, user: userData } = response.data;
          
          setToken(access_token);
          setUser(userData);
          setIsAuthenticated(true);
          setLastActivity(Date.now());
          
          // Store in localStorage
          localStorage.setItem('authToken', access_token);
          localStorage.setItem('authUser', JSON.stringify(userData));
          localStorage.setItem('lastActivity', Date.now().toString());
          
          // Set axios default header
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          
          // Start activity monitoring
          startActivityMonitoring();
          
          return {
            success: true,
            requires2FA: false,
            user: userData
          };
        }
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 2FA verification function
  const verify2FA = async (email, code) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/auth/2fa/verify-login`, {
        email,
        code
      });

      if (response.data.success) {
        const { access_token, user: userData } = response.data;
        
        setToken(access_token);
        setUser(userData);
        setIsAuthenticated(true);
        setLastActivity(Date.now());
        
        // Store in localStorage
        localStorage.setItem('authToken', access_token);
        localStorage.setItem('authUser', JSON.stringify(userData));
        localStorage.setItem('lastActivity', Date.now().toString());
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Start activity monitoring
        startActivityMonitoring();
        
        return {
          success: true,
          user: userData
        };
      } else {
        throw new Error(response.data.message || '2FA verification failed');
      }
    } catch (error) {
      console.error('2FA verification error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Call backend logout endpoint if authenticated
      if (token && isAuthenticated) {
        await axios.post(`${API}/api/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
    }
  }, [token, isAuthenticated, clearAuthData]);

  // Refresh token function
  const refreshToken = async () => {
    try {
      if (!token) {
        throw new Error('No token available');
      }

      const response = await axios.post(`${API}/api/auth/refresh-token`);
      
      if (response.data.access_token) {
        const newToken = response.data.access_token;
        setToken(newToken);
        localStorage.setItem('authToken', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        updateActivity();
        
        return newToken;
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
      throw error;
    }
  };

  // Extend session function (for when user dismisses timeout warning)
  const extendSession = () => {
    updateActivity();
    setSessionTimeoutWarning(false);
  };

  // Login with token (for auto-login after signup)
  const loginWithToken = (accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    setIsAuthenticated(true);
    setLastActivity(Date.now());
    
    // Store in localStorage
    localStorage.setItem('authToken', accessToken);
    localStorage.setItem('authUser', JSON.stringify(userData));
    localStorage.setItem('lastActivity', Date.now().toString());
    
    // Set axios default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    
    // Start activity monitoring
    startActivityMonitoring();
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    sessionTimeoutWarning,
    login,
    loginWithToken,
    verify2FA,
    logout,
    refreshToken,
    updateActivity,
    extendSession,
    clearAuthData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;